import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { TovButton } from '../TovButton/TovButton';

export function PageTitle({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return <h1 className={cn('tov-page-title', className)} {...props} />;
}

export interface PageLayoutProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title'
> {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

export function PageLayout({
  actions,
  children,
  className,
  title,
  ...props
}: PageLayoutProps) {
  return (
    <div className={cn('tov-page-layout', className)} {...props}>
      <section className="tov-page-layout__header">
        <PageTitle>{title}</PageTitle>
        {actions ? (
          <div className="tov-page-layout__actions">{actions}</div>
        ) : null}
      </section>
      {children}
    </div>
  );
}

export interface PageTitleCrumb {
  href?: string;
  label: ReactNode;
  onClick?(): void;
}

export interface PageTitleBreadcrumbProps extends HTMLAttributes<HTMLDivElement> {
  current: ReactNode;
  root: PageTitleCrumb;
}

export function PageTitleBreadcrumb({
  className,
  current,
  root,
  ...props
}: PageTitleBreadcrumbProps) {
  const rootClassName = cn(
    'tov-page-title-breadcrumb__root',
    !root.href && !root.onClick && 'tov-page-title-breadcrumb__root--static',
  );

  return (
    <div className={cn('tov-page-title-breadcrumb', className)} {...props}>
      {root.href ? (
        <a className={rootClassName} href={root.href}>
          {root.label}
        </a>
      ) : root.onClick ? (
        <TovButton className={rootClassName} onClick={root.onClick}>
          {root.label}
        </TovButton>
      ) : (
        <span className={rootClassName}>{root.label}</span>
      )}
      <span aria-hidden="true" className="tov-page-title-breadcrumb__separator">
        /
      </span>
      <span className="tov-page-title-breadcrumb__current">{current}</span>
    </div>
  );
}
